import { useShallow } from 'zustand/react/shallow';

import {
  createOfflineStore,
  selectFailedExpenses,
  selectOwnedExpenses,
  selectPendingCount,
} from '@splitfiesta/core';
import { webStorage } from '@splitfiesta/platform-web';

import { useAuthStore } from '@/stores/authStore';

export const useOfflineStore = createOfflineStore({ storage: webStorage });

// Every tab holds the queue in memory and persist rewrites all of it on each
// change, so a tab that never re-reads would put back rows another tab
// already synced or drop rows it queued.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    // A null key is another tab clearing all of localStorage.
    if (
      event.key === null ||
      event.key === useOfflineStore.persist.getOptions().name
    ) {
      void useOfflineStore.rehydrateFromOtherTab();
    }
  });
}

/** The account whose queued rows the UI may show; `undefined` signed out. */
export const useOfflineOwnerId = () =>
  useAuthStore(state => (state.isAuthenticated ? state.user?.id : undefined));

export const usePendingExpenses = () => {
  const ownerId = useOfflineOwnerId();
  return useOfflineStore(
    useShallow(state => selectOwnedExpenses(state, ownerId))
  );
};

export const usePendingCount = () => {
  const ownerId = useOfflineOwnerId();
  return useOfflineStore(state => selectPendingCount(state, ownerId));
};

export const useFailedExpenses = () => {
  const ownerId = useOfflineOwnerId();
  return useOfflineStore(
    useShallow(state => selectFailedExpenses(state, ownerId))
  );
};

export const useIsSyncing = () => useOfflineStore(state => state.isSyncing);

export const useOfflineHasHydrated = () =>
  useOfflineStore(state => state.hasHydrated);

export const usePendingExpensesByGroup = (groupId: string) => {
  const ownerId = useOfflineOwnerId();
  return useOfflineStore(
    useShallow(state => state.getPendingExpensesByGroup(groupId, ownerId))
  );
};
